import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { createPortal } from 'react-dom';
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useParams, Link, useHistory, useLocation } from "react-router-dom";
import { useAtom } from 'jotai';
import axios from 'axios';
import Container from '../Layouts/Container';
import config from '../Config';
import { useDebounce } from 'use-debounce';
import {
  FaMapMarked as FaNationality, FaRegEye as FaEye, FaRulerVertical as FaRulerCombined,
  FaThumbsUp, FaArrowUp
} from 'react-icons/fa';
import { IoIosColorPalette as FaPalette, IoMdImages } from "react-icons/io";
import { GiMedusaHead as FaHair } from "react-icons/gi";
import { LiaBirthdayCakeSolid as FaBirthdayCake } from "react-icons/lia";
import { MdOutlineDynamicFeed } from "react-icons/md";
import { TbShoppingBagPlus } from "react-icons/tb";
import FavoriteButton from '../Components/FavoriteButton';
import MosaicMediaModal from '../Components/MosaicMediaModal';
import PackMediaModal from '../Components/PackMediaModal';
import CardPack from '../Components/CardPackProfile';
import PublicPhotosGrid from '../Components/PublicPhotosGrid';
import ProfileFeedList from '../Components/ProfileFeedList';
import Loading from '../Components/Loading';
import LoadingSpinner from '../Components/LoadingSpinner';

import DarkModeContext from '../Components/DarkModeContext';
import { FaComment, FaShare } from 'react-icons/fa';
import { MdQrCode2 } from 'react-icons/md';
import Modal from "../Components/Modal";
import useDynamicMeta from '../hooks/useDynamicMeta';
import AuthRequiredModal from '../Components/AuthRequiredModal';
import ShareProfileModal from '../Components/ShareProfileModal';
import UserOptionsMenu from '../Components/safety/UserOptionsMenu';
import useUserSafety from '../Components/safety/useUserSafety';
import { STAFF_ROLES } from '../Components/safety/safetyApi';
import FlyerModal from '../Components/FlyerModal';
import { authUserAtom, isAuthAtom, dataUserAtom } from '../Services/store/atoms';

// Orden de las tabs según el contenido de cada modelo: Packs va primero
// siempre que tenga al menos uno (es lo que vende); si no tiene, o después
// de Packs, va primero la sección con más registros (en empate, Fotos antes
// que Feeds). Las tabs vacías quedan al final en este orden fijo, en gris
// y deshabilitadas (ver tabsBar).
const DEFAULT_TAB_ORDER = ['packs', 'photos', 'feeds'];
const EMPTY_TAB_COUNTS = { packs: null, feeds: null, photos: null };

function computeTabOrder(counts) {
  const withContent = DEFAULT_TAB_ORDER.filter(id => counts[id] > 0);
  const empty = DEFAULT_TAB_ORDER.filter(id => !(counts[id] > 0));
  // sort es estable: en empate se respeta DEFAULT_TAB_ORDER.
  withContent.sort((a, b) => {
    if (a === 'packs') return -1;
    if (b === 'packs') return 1;
    return counts[b] - counts[a];
  });
  return [...withContent, ...empty];
}

function ProfileModel({ inModal = false, userIdToLoad = null }) {
  const params = useParams();
  const idModel = userIdToLoad || params.idModel;
  const topRef = useRef(null);
  const tabsRef = useRef(null);

  // El banner+tabs viven como contenido NORMAL (primer hijo) dentro del
  // scroll propio de CADA tab — así "desaparecer" es simplemente scrollearlo
  // fuera de vista, sin ninguna animación/overlay artificial: un solo
  // origen de movimiento (el scroll real del navegador), cero riesgo de que
  // el contenido se mueva "de más" o quede tapado por otra capa. tabsBar es
  // sticky top-0 DENTRO de ese mismo contenedor que scrollea, así que sticky
  // funciona de forma nativa y correcta.
  //
  // Como cada tab tiene su PROPIO scroll independiente (para recordar por
  // separado dónde se quedó el usuario en cada una), pero el colapso del
  // banner debe sentirse COMPARTIDO entre las tres, hay que sincronizarlas.
  // OJO: no se puede asignar scrollTop a una tab OCULTA (`hidden` = display:
  // none) esperando que "se guarde" para cuando se muestre — mientras está
  // oculta, scrollHeight/clientHeight son 0, así que cualquier asignación
  // se clampea a 0 al instante (verificado). Por eso la sincronización pasa
  // por otro lado: se guarda solo el estado compartido (bannerCollapsedRef),
  // y un efecto separado (más abajo) corrige el scrollTop de la tab recién
  // hecha VISIBLE, justo cuando cambia activeTab — momento en el que ya
  // tiene layout real y la asignación sí funciona.
  const packsPanelRef = useRef(null);
  const feedsPanelRef = useRef(null);
  const photosPanelRef = useRef(null);
  const panelRefsByTab = { packs: packsPanelRef, feeds: feedsPanelRef, photos: photosPanelRef };
  const bannerCollapsedRef = useRef(false);
  // Último scrollTop que NOSOTROS le impusimos a cada tab (o su valor
  // inicial, 0, si nunca la tocamos) — permite distinguir "el usuario ya
  // scrolleó esta tab por su cuenta desde la última sincronización" (no
  // tocar, respetar su posición real) de "sigue exactamente donde la
  // dejamos" (es seguro re-sincronizarla al nuevo estado compartido).
  const lastSyncedScrollByTab = useRef({ packs: 0, feeds: 0, photos: 0 });
  // Cuando una tab NO tiene contenido real para scrollear (ej. "Feeds" sin
  // publicaciones), no hay forma de "scrollear el banner fuera de vista"
  // ahí — el scrollTop se queda clampeado en 0 sin importar qué le
  // asignemos. Para esas tabs, en vez de dejar el banner completo mostrado
  // (rompiendo la sensación de "colapsado compartido" y pareciendo que el
  // modal "se resetea"), lo ocultamos directamente por estado.
  const [forceHiddenBannerTabs, setForceHiddenBannerTabs] = useState({ packs: false, feeds: false, photos: false });

  const packsHeaderRef = useRef(null);
  const feedsHeaderRef = useRef(null);
  const photosHeaderRef = useRef(null);
  const headerRefsByTab = { packs: packsHeaderRef, feeds: feedsHeaderRef, photos: photosHeaderRef };
  const [headerHeight, setHeaderHeight] = useState(0);
  const [authUser] = useAtom(authUserAtom);
  const [isAuth] = useAtom(isAuthAtom);
  const [dataUser] = useAtom(dataUserAtom);
  const history = useHistory();
  const location = useLocation();
  const { darkMode } = useContext(DarkModeContext);
  const modalRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [modelo, setModelo] = useState(null);
  const [search, setSearch] = useState("");
  const [value] = useDebounce(search, 1000);
  const [favorite, setFavorite] = useState(false);
  const [feeds, setFeeds] = useState([]);
  const [totalFeeds, setTotalFeeds] = useState(0);
  const [nextFeedsPageUrl, setNextFeedsPageUrl] = useState(null);
  const [isLoadingFeeds, setIsLoadingFeeds] = useState(false);
  const [activeTab, setActiveTab] = useState('packs');
  // Total real de cada sección (null = todavía no respondió su endpoint).
  // El orden de las tabs se decide recién cuando llegan los tres, así la
  // tab inicial no salta de una a otra a medida que van respondiendo.
  const [tabCounts, setTabCounts] = useState(EMPTY_TAB_COUNTS);
  const [tabOrder, setTabOrder] = useState(null);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showModal, setShowModal] = useState(false);
  useBodyScrollLock(showModal);
  const [selectedPack, setSelectedPack] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Mide la altura real del banner+nickname (idéntica en las 3 tabs, mismos
  // datos) para saber a qué scrollTop equivale "banner colapsado" en las
  // tabs que se sincronizan. Solo la tab ACTIVA está realmente renderizada
  // con tamaño (las otras están en `display:none`, miden 0), así que el
  // observer se re-suscribe a la tab activa cada vez que cambia.
  //
  // forceHiddenBannerTabs[activeTab] TAMBIÉN está en las dependencias:
  // cuando esa tab oculta su banner por estado, el div que este observer
  // está observando se DESMONTA del DOM. Sin desconectar el observer en ese
  // mismo momento (el cleanup de este efecto), sigue "colgado" mirando ese
  // nodo ya removido, que reporta tamaño 0 justo antes de perderlo del
  // todo — ese 0 pisaba headerHeight y hacía que el cálculo de "cuánto
  // scroll hace falta para colapsar" se invirtiera, revelando el banner de
  // nuevo (el bug reportado: la tab volvía a "resetearse" sola).
  useEffect(() => {
    if (!inModal) return;
    if (forceHiddenBannerTabs[activeTab]) return;
    const el = headerRefsByTab[activeTab]?.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setHeaderHeight(entries[0].contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [inModal, activeTab, forceHiddenBannerTabs[activeTab]]);

  // Corrige el scrollTop de la tab que ACABA de volverse visible para que
  // coincida con el estado compartido de colapso (ver comentario junto a
  // lastSyncedScrollByTab más arriba). Solo la toca si sigue exactamente
  // donde la dejamos la última vez — si el usuario ya la scrolleó por su
  // cuenta desde entonces, se respeta esa posición real sin pisarla.
  useEffect(() => {
    if (!inModal) return;
    const el = panelRefsByTab[activeTab]?.current;
    if (!el) return;

    const expectedTarget = bannerCollapsedRef.current ? headerHeight : 0;
    // Capacidad REAL de scroll de esta tab, independiente de dónde esté
    // parado su scrollTop ahora mismo (que puede ser la posición real del
    // usuario, no nuestro objetivo). Solo se oculta el banner por estado si
    // la tab prácticamente NO tiene scroll (mismo umbral que
    // handleTabScroll). Si tiene algo aunque no alcance para esconder el
    // banner entero (ej. 7 fotos), se scrollea hasta donde dé: ocultarlo ahí
    // la dejaba sin nada que scrollear y el banner ya no volvía en ninguna
    // tab.
    const maxScrollable = el.scrollHeight - el.clientHeight;
    const shouldForceHide = bannerCollapsedRef.current && maxScrollable <= 20;
    const effectiveTarget = shouldForceHide ? 0 : expectedTarget;

    if (el.scrollTop === lastSyncedScrollByTab.current[activeTab] && el.scrollTop !== effectiveTarget) {
      el.scrollTop = effectiveTarget;
      // Valor real tras el clamp del navegador (puede quedar por debajo de
      // effectiveTarget si la tab no tiene tanto scroll).
      lastSyncedScrollByTab.current[activeTab] = el.scrollTop;
    }

    setForceHiddenBannerTabs(prev => (prev[activeTab] === shouldForceHide ? prev : { ...prev, [activeTab]: shouldForceHide }));
  }, [inModal, activeTab, headerHeight]);

  useDynamicMeta({
    title: modelo ? `${modelo.nickname} - Yourpack` : 'Yourpack',
    description: modelo ? `Mira el perfil de ${modelo.nickname} en Yourpack` : 'Yourpack - Contenido exclusivo',
    image: modelo?.profile_photo || undefined,
    url: modelo?.nickname ? `${window.location.origin}/${modelo.nickname}` : undefined,
  });

  const observer = useRef();
  const lastProductElementRef = useCallback(node => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Solo registra el primer valor que llega para cada sección: un error
  // posterior (ej. getFavorite después de cargar el perfil) no pisa un
  // total ya conocido.
  const resolveTabCount = (tab, count) => {
    setTabCounts(prev => (prev[tab] === null ? { ...prev, [tab]: count } : prev));
  };

  const fetchProducts = useCallback(() => {
    if (!hasMore) return;
    setIsLoading(true);

    axios.get(`${config.REACT_APP_BASE_URL}/api/v1/packs/listById/${idModel}?page=${page}`)
      .then((response) => {
        const newProducts = response.data.data;
        setProducts(prevProducts => {
          const uniqueProducts = [...new Set([...prevProducts, ...newProducts].map(product => product.pack_id))].map(id => {
            return [...prevProducts, ...newProducts].find(product => product.pack_id === id);
          });
          return uniqueProducts;
        });
        setHasMore(newProducts.length > 0);
        // total del paginador (no products.length, que solo cuenta lo ya
        // cargado: máximo 12 por página).
        if (page === 1) resolveTabCount('packs', response.data.total ?? newProducts.length);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        // 403 por país/VPN/modo fantasma: se trata como sin packs.
        if (page === 1) resolveTabCount('packs', 0);
        setIsLoading(false);
      });
  }, [idModel, page, hasMore]);

  const fetchModelData = async () => {
    try {
      const modelResponse = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/home/profileModelById/${idModel}`);
      setModelo(modelResponse.data);
      resolveTabCount('photos', modelResponse.data?.public_images?.length || 0);

      if (isAuth) {
        const favoriteResponse = await axios.get(`${config.REACT_APP_BASE_URL}/api/v1/home/getFavorite/${idModel}`);
        setFavorite(!!favoriteResponse.data);
      }
    } catch (error) {
      console.error(error);
      resolveTabCount('photos', 0);
    }
  };

  const fetchFeeds = async (pageUrl) => {
    setIsLoadingFeeds(true);
    try {
      const response = await axios.get(pageUrl || `${config.REACT_APP_BASE_URL}/api/v1/home/FeedByUser/${idModel}`);
      setFeeds(prevFeeds => {
        const newFeeds = response.data.feeds.data;
        if (!pageUrl) return newFeeds;
        const unique = newFeeds.filter(f => !prevFeeds.some(p => p.id === f.id));
        return [...prevFeeds, ...unique];
      });
      setTotalFeeds(response.data.totalFeeds);
      setNextFeedsPageUrl(response.data.feeds.next_page_url);
      if (!pageUrl) resolveTabCount('feeds', response.data.totalFeeds || 0);
    } catch (err) {
      setError(err.message);
      if (!pageUrl) resolveTabCount('feeds', 0);
    } finally {
      setIsLoadingFeeds(false);
    }
  };

  useEffect(() => {
    fetchModelData();
    fetchFeeds();
    setProducts([]);
    setPage(1);
    setHasMore(true);
    setTabCounts(EMPTY_TAB_COUNTS);
    setTabOrder(null);
  }, [idModel, value]);

  // Se decide una sola vez por perfil y queda fija: no se recalcula al
  // cargar más páginas de packs o feeds.
  useEffect(() => {
    if (tabOrder) return;
    if (Object.values(tabCounts).some(count => count === null)) return;
    const order = computeTabOrder(tabCounts);
    setTabOrder(order);
    setActiveTab(order[0]);
  }, [tabCounts, tabOrder]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, page]);

  // Solo aplica a la página de perfil completa: ahí las tres tabs comparten
  // un único scroll (el de la ventana) con la barra de tabs sticky arriba,
  // así que sin este reset, cambiar de una tab larga (con scroll) a una
  // corta deja al usuario mirando el hueco vacío del min-h-dvh en vez del
  // contenido real de la tab nueva. Se ACOTA el scroll como máximo hasta
  // donde empiezan los tabs (no hasta el tope absoluto): si todavía se ve
  // el banner no se toca nada; si ya estaban pegados arriba, quedan igual
  // de pegados en vez de saltar hasta el banner.
  //
  // En el modal esto ya no hace falta: cada tab tiene su propio contenedor
  // de scroll independiente (ver el return más abajo) que conserva su
  // posición solo, sin necesidad de este truco.
  useEffect(() => {
    if (inModal) return;

    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const tabsAbsoluteTop = tabsEl.getBoundingClientRect().top + window.scrollY;
    window.scrollTo(0, Math.min(window.scrollY, tabsAbsoluteTop));
  }, [activeTab, inModal]);

  const renderIcon = (icon) => {
    switch (icon) {
      case 'thumbs':
        return <FaThumbsUp />;
      case 'age':
        return <FaBirthdayCake />;
      case 'ethnicity':
        return <FaNationality />;
      case 'skin_color':
        return <FaPalette />;
      case 'eye_color':
        return <FaEye />;
      case 'hair_color':
        return <FaHair />;
      case 'body_measurements':
      case 'height':
        return <FaRulerCombined />;
      default:
        return null;
    }
  };

  const handleFavoriteClick = async () => {
    if (isAuth) {
      try {
        const response = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/home/storeFavorites/${idModel}`);
        setFavorite(response.data.isFavorite);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const clickPack = (pack) => {
    if (isAuth) {
      setScrollPosition(window.pageYOffset);
      setSelectedPack(pack);
      setShowModal(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleBuyPack = () => {
    setShowModal(false);
    const buyRole = ['client', 'user'].includes(dataUser?.role) ? dataUser.role : 'client';
    history.push(`/${buyRole}/buyPack/${selectedPack.pack_id}`);
  };

  const handleCancelPurchase = () => {
    setShowModal(false);
    window.scrollTo(0, scrollPosition);
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    history.push('/login');
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleMediaClick = async (packId) => {
    let allFiles = [];
    try {
      const response = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/packs/pack/${packId}`);
      allFiles = (response.data.files || [])
        .map(f => ({
          url: f.preview_file || f.processed_file,
          file_format: f.file_type,
        }))
        .filter(f => f.url);
    } catch {}

    // push (no replace): así el preview queda en su propia entrada de
    // historial y "atrás"/Escape/gesto solo cierra el preview, sin arrastrar
    // consigo el modal de perfil que está debajo (mismo patrón que
    // showAlbumModal/openPublicAlbum más abajo).
    history.push({
      pathname: location.pathname,
      search: location.search,
      hash: '#media',
      state: { ...location.state, fluidMedia: allFiles }
    });
  };

  // Empuja su propia entrada de historial (en vez de estado local) para que
  // "atrás" (físico en Android/iOS, o Escape) solo cierre el visor y no
  // arrastre consigo el modal de perfil que está debajo — mismo patrón que
  // usa MosaicMediaModal para el visor de fotos de perfil/portada.
  const showAlbumModal = !!location.state?.albumViewer;
  const albumStartIndex = location.state?.albumViewerIndex || 0;

  const openPublicAlbum = (index = 0) => {
    history.push({
      pathname: location.pathname,
      search: location.search,
      hash: '#album',
      state: { ...location.state, albumViewer: true, albumViewerIndex: index }
    });
  };

  const closePublicAlbum = () => {
    history.goBack();
  };

  const handleStartChat = async () => {
    if (!isAuth) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/start/${modelo.user_id}`, {}, {
        headers: {
          Authorization: `Bearer ${authUser.token}`
        }
      });

      const conversationId = response.data.id;
      const currentSection = location.pathname.split('/')[1];
      const section = dashboardBase.includes(currentSection) ? currentSection : dataUser?.role;
      history.push(`/${section}/chat/${conversationId}`);
    } catch (error) {
      console.error('Error iniciando el chat:', error);
    }
  };

  // Verificar si el usuario es un administrador
  const isAdmin = dataUser?.role === 'model';

  // Reportar y bloquear (Components/safety, como la app móvil): cualquier
  // usuario autenticado que no sea staff, sobre un perfil ajeno. Al terminar
  // de bloquear se sale del perfil, porque deja de estar visible.
  const canBlock = isAuth && !!modelo && authUser?.id !== modelo.user_id
    && !STAFF_ROLES.includes(dataUser?.role);
  const chatSection = () => {
    const currentSection = location.pathname.split('/')[1];
    return dashboardBase.includes(currentSection) ? currentSection : dataUser?.role;
  };
  const safety = useUserSafety({
    user: modelo ? { id: modelo.user_id, nickname: modelo.nickname } : null,
    canBlock,
    onBlockFinished: () => history.goBack(),
    onGoToChat: (chatId) => history.push(`/${chatSection()}/chat/${chatId}`),
  });

  const dashboardBase = ['model', 'client', 'admin', 'user', 'assistant'];
  const inDashboard = dashboardBase.includes(location.pathname.split('/')[1]);

  // Banner + nickname/botones, separado de la barra de tabs a propósito: en
  // el modal cada tab lo incluye DENTRO de su propio scroll (así se va al
  // scrollear, igual que antes, y cada tab lo recuerda por separado en vez
  // de quedar fijo comiéndose espacio de pantalla todo el tiempo). En
  // página completa se usan los dos juntos, sin cambios respecto a antes.
  const bannerAndNickname = modelo && (
    <>
      <div className="px-0 md:px-1">
        {/* Banner Container */}
        <div className={`relative ${inModal ? 'pt-0' : 'pt-4'}`}>
          <MosaicMediaModal mediaUrls={modelo.files} avatarSrc={modelo.avatar_medium_url} totalFeeds={totalFeeds} idModel={idModel} inModal={inModal} />

          {/* Favorito: arriba a la derecha (más chico) en mobile, abajo
              a la derecha desde md. */}
          <div className="absolute top-2 right-2 md:top-auto md:bottom-4 md:right-4 z-20">
            <FavoriteButton
              idModel={modelo.user_id}
              className="bg-white dark:bg-gray-800 p-1 md:p-1.5 rounded-full shadow-sm hover:scale-110 transition-transform text-sm md:text-base"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:px-6 my-4">
          {/* Nickname: trunca en vez de forzar overflow/scroll horizontal
              cuando es largo, y reduce tamaño en mobile para no competir
              por espacio con los botones de la derecha. */}
          <div
            className="text-2xl sm:text-4xl text-rose-500 font-semibold capitalize truncate max-w-[55%] sm:max-w-none"
            translate="no"
          >
            {modelo.nickname}
          </div>

          {/* Botones derecha: compartir + chat */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Compartir - visible para todos */}
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 transition-colors shadow"
              aria-label="Compartir perfil"
            >
              <FaShare className="text-base" />
            </button>

            {canBlock && (
              <UserOptionsMenu
                user={{ id: modelo.user_id, nickname: modelo.nickname }}
                onReport={safety.openReport}
                onBlock={safety.openBlock}
              />
            )}

            {/* Flyer publicitario — solo visible para la modelo y el admin */}
            {isAuth && (authUser.id === modelo?.user_id || dataUser?.role === 'admin') && (
              <button
                onClick={() => setShowFlyerModal(true)}
                className="flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-rose-500 hover:bg-rose-600 text-white transition-colors shadow-sm"
                aria-label="Generar flyer"
                title="Generar flyer publicitario"
              >
                <MdQrCode2 className="text-xl sm:text-2xl" />
              </button>
            )}

            {/* Chat para usuarios autenticados */}
            {isAuth && authUser.id !== modelo.user_id && (
              <button
                onClick={handleStartChat}
                className={`z-30 flex items-center space-x-2 sm:space-x-3 bg-rose-500 hover:bg-rose-600 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-sm hover:shadow-xl transition-all duration-300 ${isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-label="Chat"
                disabled={isAdmin}
              >
                <FaComment className="text-xl sm:text-2xl" />
                <span className="font-medium text-base sm:text-lg">Chat</span>
              </button>
            )}

            {/* Chat para usuarios no autenticados */}
            {!isAuth && (
              <button
                onClick={() => setShowAuthModal(true)}
                className="z-30 flex items-center space-x-2 sm:space-x-3 bg-rose-500 hover:bg-rose-600 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-sm hover:shadow-xl transition-all duration-300"
                aria-label="Chat"
              >
                <FaComment className="text-xl sm:text-2xl" />
                <span className="font-medium text-base sm:text-lg">Chat</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Barra de tabs sola. min-w-0 en cada botón es necesario: sin eso, un flex
  // item nunca se achica por debajo del tamaño de su propio contenido
  // (default min-width:auto de flexbox), así que con contadores de 2
  // dígitos ("Feeds (19)") el contenido no entraba en el tercio disponible
  // en mobile y empujaba toda la píldora más ancha que la pantalla,
  // desbordando el último tab ("Fotos") fuera del borde derecho.
  //
  // Sticky de verdad: en el modal vive DENTRO del scroll propio de cada tab
  // (junto al banner+nickname, en orden normal), así que al bajar y pasar
  // el banner queda pegada arriba del scroll de esa tab en particular — no
  // fija desde el arranque, solo mientras hay contenido que scrollear. Como
  // el ancestro que scrollea es el panel de esa misma tab (sin ningún
  // overflow-hidden intermedio), sticky funciona sin el riesgo que tenía
  // antes de romperse por un ancestro raro. En página completa sigue el
  // mismo patrón, pero sobre el scroll compartido de la ventana (por eso el
  // offset es top-14, para no quedar debajo del header fijo del dashboard).
  //
  // Mientras no se decidió el orden (ver computeTabOrder) la píldora queda
  // `invisible`: ocupa su lugar (sin salto de layout) pero no se ve un orden
  // provisorio que después se reacomoda.
  const tabDefs = {
    packs: { id: 'packs', label: 'Packs', icon: <TbShoppingBagPlus className="text-base sm:text-lg shrink-0" />, count: tabCounts.packs || 0 },
    feeds: { id: 'feeds', label: 'Feeds', icon: <MdOutlineDynamicFeed className="text-base sm:text-lg shrink-0" />, count: totalFeeds },
    photos: { id: 'photos', label: 'Fotos', icon: <IoMdImages className="text-base sm:text-lg shrink-0" />, count: modelo?.public_images?.length || 0 },
  };

  // Sin margen negativo: el panel que la contiene no tiene padding, y el -mx-4
  // la hacía 32 px más ancha que la pantalla (scroll lateral en el celular).
  const tabsBar = (
    <div
      ref={tabsRef}
      className={`sticky z-30 px-4 pt-2 pb-2 sm:px-6 flex justify-center ${
        inModal ? 'top-0 bg-white dark:bg-gray-800' : 'top-14 bg-gray-100 dark:bg-gray-900'
      }`}
    >
      <div className={`flex w-full max-w-md bg-gray-100 dark:bg-gray-800 rounded-full p-1 gap-1 ${tabOrder ? '' : 'invisible'}`}>
        {(tabOrder || DEFAULT_TAB_ORDER).map(id => tabDefs[id]).map(tab => {
          // Tab sin contenido: gris y deshabilitada (computeTabOrder ya la
          // deja al final), así no se entra a una sección vacía. La activa
          // nunca se deshabilita: si la modelo no tiene nada en ninguna
          // sección, el perfil abre igual en Packs vacío.
          const disabled = tab.count === 0 && activeTab !== tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={disabled}
              title={disabled ? 'Sin contenido todavía' : undefined}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 py-2 px-1.5 sm:px-2 rounded-full text-xs sm:text-sm font-medium transition-colors overflow-hidden ${
                activeTab === tab.id
                  ? 'bg-rose-500 text-white shadow'
                  : disabled
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {tab.icon}
              <span className="truncate">{tab.label}</span>
              {tab.count > 0 && <span className="text-[0.65rem] sm:text-xs opacity-80 shrink-0">({tab.count})</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const packsBody = (
    <div className={`flex justify-center ${inModal ? '' : 'min-h-dvh'} pt-6 sm:px-6`}>
      {/* Packs es la tab visible mientras se decide el orden: spinner en vez
          de "no hay packs" para no mostrar un vacío falso. */}
      {!tabOrder ? (
        <div className="py-16">
          <LoadingSpinner />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 py-16">
          Todavía no hay packs disponibles.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 w-full max-w-7xl justify-items-center">
          {products.map((product, index) => (
            <div
              key={product.pack_id}
              ref={products.length === index + 1 ? lastProductElementRef : null}
              className="w-full flex justify-center" // Contenedor adicional para centrado
            >
              <CardPack pack={product} onClick={clickPack} onMediaClick={handleMediaClick} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const feedsBody = (
    <div className={`flex justify-center ${inModal ? '' : 'min-h-dvh'} pt-6 sm:px-6`}>
      <ProfileFeedList
        feeds={feeds}
        isLoading={isLoadingFeeds}
        hasMore={!!nextFeedsPageUrl}
        onLoadMore={() => fetchFeeds(nextFeedsPageUrl)}
      />
    </div>
  );

  const photosBody = (
    <div className={`flex justify-center ${inModal ? '' : 'min-h-dvh'} pt-6 sm:px-6`}>
      <PublicPhotosGrid
        images={modelo?.public_images || []}
        onPhotoClick={(index) => openPublicAlbum(index)}
      />
    </div>
  );

  // El banner ya no se "colapsa" con una animación aparte: vive como
  // contenido normal arriba de cada lista, así que desaparece simplemente
  // porque el usuario lo scrollea fuera de vista (un solo movimiento, el
  // scroll real, cero riesgo de que algo se mueva "de más"). Acá solo se
  // actualiza el estado COMPARTIDO (bannerCollapsedRef); la tab que aún no
  // se visitó se sincroniza sola cuando se muestra (ver el efecto de arriba
  // que depende de activeTab) — no se puede tocar el scrollTop de las otras
  // dos tabs desde acá porque están ocultas con `hidden` (display:none), y
  // asignar scrollTop mientras un elemento está oculto se clampea a 0 al
  // instante (scrollHeight/clientHeight son 0 sin layout real).
  //
  // Guarda contra el parpadeo: si el panel no tiene contenido real para
  // scrollear (scrollHeight ~ clientHeight), cualquier scrollTop != 0 es
  // rebote elástico del navegador (iOS/Android) al hacer el gesto sin nada
  // que mover, no intención real de scroll — se ignora por completo.
  const handleTabScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.clientHeight <= 20) return;

    // Histéresis (colapsa pasando 40px, expande bajando de 5px) para que el
    // jitter normal del scroll cerca del umbral no dispare cambios de
    // estado una y otra vez.
    const wasCollapsed = bannerCollapsedRef.current;
    bannerCollapsedRef.current = wasCollapsed ? el.scrollTop > 5 : el.scrollTop > 40;
  };

  // En una tab con el banner oculto por estado (sin scroll propio), el
  // gesto de "subir" (rueda hacia arriba o deslizar el dedo hacia abajo)
  // no genera ningún evento scroll, así que se detecta a mano y se vuelve a
  // mostrar el banner, expandiendo el estado compartido para todas las tabs.
  const touchStartYRef = useRef(null);
  const revealBanner = () => {
    if (!forceHiddenBannerTabs[activeTab]) return;
    bannerCollapsedRef.current = false;
    setForceHiddenBannerTabs(prev => ({ ...prev, [activeTab]: false }));
  };
  const panelScrollHandlers = {
    onScroll: handleTabScroll,
    onWheel: (e) => { if (e.deltaY < 0) revealBanner(); },
    onTouchStart: (e) => { touchStartYRef.current = e.touches[0].clientY; },
    onTouchMove: (e) => {
      if (touchStartYRef.current !== null && e.touches[0].clientY - touchStartYRef.current > 30) revealBanner();
    },
  };

  return (
    <div ref={topRef} className={inModal ? 'h-full flex flex-col overflow-hidden' : undefined}>
      {inModal ? (
        <>
          {/* Banner+tabs como contenido NORMAL arriba de cada lista (ver
              comentario junto a panelRefsByTab más arriba): "desaparecer" es
              simplemente scrollearlo fuera de vista, un solo movimiento, sin
              overlays ni animaciones que puedan desincronizarse del scroll
              real y sumar un salto extra. */}
          <div className="flex-1 min-h-0 relative">
            <div
              ref={packsPanelRef}
              className={`absolute inset-0 overflow-y-auto overflow-x-hidden profile-modal-scroll-panel ${activeTab === 'packs' ? '' : 'hidden'}`}
              {...panelScrollHandlers}
            >
              {!forceHiddenBannerTabs.packs && (
                <div ref={packsHeaderRef} className="dark:bg-gray-800 text-black dark:text-white">
                  <Container noMargin>{bannerAndNickname}</Container>
                </div>
              )}
              {tabsBar}
              <div className="container mx-auto pb-10 px-4 md:px-0 lg:px-0">{packsBody}</div>
            </div>
            <div
              ref={feedsPanelRef}
              className={`absolute inset-0 overflow-y-auto overflow-x-hidden profile-modal-scroll-panel ${activeTab === 'feeds' ? '' : 'hidden'}`}
              {...panelScrollHandlers}
            >
              {!forceHiddenBannerTabs.feeds && (
                <div ref={feedsHeaderRef} className="dark:bg-gray-800 text-black dark:text-white">
                  <Container noMargin>{bannerAndNickname}</Container>
                </div>
              )}
              {tabsBar}
              <div className="container mx-auto pb-10 px-4 md:px-0 lg:px-0">{feedsBody}</div>
            </div>
            <div
              ref={photosPanelRef}
              className={`absolute inset-0 overflow-y-auto overflow-x-hidden profile-modal-scroll-panel ${activeTab === 'photos' ? '' : 'hidden'}`}
              {...panelScrollHandlers}
            >
              {!forceHiddenBannerTabs.photos && (
                <div ref={photosHeaderRef} className="dark:bg-gray-800 text-black dark:text-white">
                  <Container noMargin>{bannerAndNickname}</Container>
                </div>
              )}
              {tabsBar}
              <div className="container mx-auto pb-10 px-4 md:px-0 lg:px-0">{photosBody}</div>
            </div>
          </div>

          {showAlbumModal && (
            <PackMediaModal
              show={true}
              onClose={closePublicAlbum}
              mediaUrls={modelo?.public_images || []}
              startIndex={albumStartIndex}
            />
          )}
          {error && <p className="text-red-500 px-4">{error}</p>}
        </>
      ) : (
        <div className="flex dark:bg-gray-900 text-black dark:text-white">
          <Container noMargin={inDashboard}>
            {bannerAndNickname}
            {tabsBar}
            {activeTab === 'packs' && packsBody}
            {activeTab === 'feeds' && feedsBody}
            {activeTab === 'photos' && photosBody}

            {showAlbumModal && (
              <PackMediaModal
                show={true}
                onClose={closePublicAlbum}
                mediaUrls={modelo?.public_images || []}
                startIndex={albumStartIndex}
              />
            )}
            {error && <p className="text-red-500">{error}</p>}
          </Container>
        </div>
      )}

      {/* Pack Purchase Modal */}
      {showModal && createPortal(
        <div className="fixed z-confirm inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      ¿Do you want to buy this pack?
                    </h3>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-rose-600 text-base font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleBuyPack}
                >
                  BUY
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-gray-200 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCancelPurchase}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Login Required Modal */}
      {showLoginModal && createPortal(
        <div className="fixed z-confirm inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      To make purchases on the page you must be logged in
                    </h3>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-full border border-transparent shadow-sm px-4 py-2 bg-rose-600 text-base font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleLoginModalClose}
                >
                  Login
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-gray-200 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCloseLoginModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <Modal ref={modalRef} onClose={() => {/* Aquí puede ir alguna lógica adicional al cerrar el modal */ }} />
      
      <AuthRequiredModal
        show={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        title="Acceso Restringido"
        message="Debes iniciar sesión para chatear, ver el perfil completo y comprar packs. Una vez autenticado podrás navegar libremente por la página."
        resumeAction={modelo ? { type: 'startChat', modelUserId: modelo.user_id } : null}
      />

      {safety.modals}

      <ShareProfileModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        nickname={modelo?.nickname}
      />

      <FlyerModal
        isOpen={showFlyerModal}
        onClose={() => setShowFlyerModal(false)}
        nickname={modelo?.nickname}
        profilePhoto={modelo?.profile_photo}
        photos={(() => {
          const isImg = (u) => u && /\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(u);
          const seen = new Set();
          const add = (url) => { if (url && isImg(url) && !seen.has(url)) { seen.add(url); } };
          if (modelo?.profile_photo) add(modelo.profile_photo);
          (modelo?.files || []).forEach(add);
          feeds.forEach(f => (f.files || []).forEach(file => add(file.url)));
          products.forEach(p => (p.files || []).forEach(f => { add(f.preview_file); add(f.processed_file); }));
          return [...seen];
        })()}
      />
    </div>
  );
}

export default ProfileModel;
